import React, { useState } from "react";
import { Collection, ChromeTab } from "@/types";
import TabCard from "./TabCard";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { cn } from "@/lib/utils";
import ConfirmDeleteDialog from "../common/ConfirmDeleteDialog";

interface CollectionSectionProps {
  collection: Collection;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onUpdateCollection?: (updatedCollection: Collection) => void;
  onDeleteCollection?: (collectionId: string) => void;
  onDeleteTab?: (collectionId: string, tabId: string) => void;
}

const CollectionSection = ({ 
  collection, 
  isExpanded,
  onToggleExpand,
  onUpdateCollection,
  onDeleteCollection,
  onDeleteTab
}: CollectionSectionProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Use droppable on the main collection area
  const { setNodeRef, isOver } = useDroppable({
    id: `empty-drop-${collection.collection_id}`,
    data: { type: 'empty-drop', collectionId: collection.collection_id },
  });

  const handleDeleteCollection = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCollection = () => {
    onDeleteCollection?.(collection.collection_id);
    setShowDeleteConfirm(false);
  };

  const handleDeleteTab = (tabId: string) => {
    onDeleteTab?.(collection.collection_id, tabId);
  };

  return (
    <>
      <div
        ref={setNodeRef}
        className={cn(
          "space-y-3 py-2 transition-colors",
          isOver && "ring-2 ring-hintword-orange bg-accent/30 rounded-xl"
        )}
      >
        <div 
          onClick={onToggleExpand}
          className={cn(
            "collection-header",
            "flex items-center justify-between py-3 px-4",
            "hover:bg-accent/40 rounded-lg",
            "cursor-pointer transition-colors",
            "w-full group"
          )}
        >
          <div className="flex items-center gap-3">
            <span className="text-lg font-medium text-hintword-orange">{collection.name}</span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{collection.tabs?.length || 0} tabs</span>
            {onDeleteCollection && (
              <button
                onClick={handleDeleteCollection}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-destructive/10 rounded-lg"
                title="Delete collection"
              >
                <Trash2 className="w-4 h-4 text-destructive" />
              </button>
            )}
          </div>
        </div>
        
        {isExpanded && (
          <div className="px-2">
            {(collection.tabs ?? []).length === 0 ? (
              <div className="h-24 flex items-center justify-center text-muted-foreground border-2 border-dashed rounded-lg border-border/50">
                Drop tabs here
              </div>
            ) : (
              <SortableContext 
                items={(collection.tabs ?? []).map(tab => `tab-drop-${collection.collection_id}-${tab.tab_id}`)}
                strategy={verticalListSortingStrategy}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {(collection.tabs ?? []).map((tab) => (
                    <TabCard 
                      key={`collection-${collection.collection_id}-${tab.tab_id}`}
                      tab={tab}
                      collectionId={collection.collection_id}
                    />
                  ))}
                </div>
              </SortableContext>
            )}
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={showDeleteConfirm}
        title="Delete Collection"
        description={`Are you sure you want to delete "${collection.name}" and all its tabs? This action cannot be undone.`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteCollection}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default CollectionSection;
